// Ward rounds and nursing (docs/plans/phase2-ward-rounds-nursing.md §3.3).
import { queryOptions } from '@tanstack/react-query';
import {
  handoverNotesSchema,
  nursingViewSchema,
  wardRoundStartedSchema,
  wardWorklistSchema,
  type RecordHandoverBody,
  type RecordNursingNoteBody,
  type recordVitalsRequestSchema,
} from '@wodi/shared';
import type { z } from 'zod';

import { api } from './client.ts';

/** The worklist refreshes every minute; ward work moves slower than a queue. */
export const WORKLIST_REFRESH_MS = 60_000;

export const wardWorklistQuery = (wardId: string) =>
  queryOptions({
    queryKey: ['inpatient', 'worklist', wardId],
    queryFn: () =>
      api('GET', `/inpatient/wards/${wardId}/worklist`, { schema: wardWorklistSchema }),
    refetchInterval: WORKLIST_REFRESH_MS,
  });

export const handoverNotesQuery = (wardId: string) =>
  queryOptions({
    queryKey: ['inpatient', 'handover', wardId],
    queryFn: () =>
      api('GET', `/inpatient/wards/${wardId}/handover-notes`, { schema: handoverNotesSchema }),
  });

export const nursingQuery = (admissionId: string) =>
  queryOptions({
    queryKey: ['inpatient', 'nursing', admissionId],
    queryFn: () => api('GET', `/admissions/${admissionId}/nursing`, { schema: nursingViewSchema }),
  });

export const startWardRound = (admissionId: string, clientRequestId: string) =>
  api('POST', `/admissions/${admissionId}/ward-rounds`, {
    body: { clientRequestId },
    schema: wardRoundStartedSchema,
  });

export const recordVitalsRound = (
  admissionId: string,
  body: z.input<typeof recordVitalsRequestSchema>,
) => api('POST', `/admissions/${admissionId}/vitals`, { body, schema: nursingViewSchema });

export const recordNursingNote = (admissionId: string, body: RecordNursingNoteBody) =>
  api('POST', `/admissions/${admissionId}/nursing-notes`, { body, schema: nursingViewSchema });

export const nursingNoteInError = (noteId: string, reason: string) =>
  api('POST', `/nursing-notes/${noteId}/entered-in-error`, {
    body: { reason },
    schema: nursingViewSchema,
  });

export const recordHandoverNote = (wardId: string, body: RecordHandoverBody) =>
  api('POST', `/inpatient/wards/${wardId}/handover-notes`, { body, schema: handoverNotesSchema });

export const handoverNoteInError = (noteId: string, reason: string) =>
  api('POST', `/handover-notes/${noteId}/entered-in-error`, {
    body: { reason },
    schema: handoverNotesSchema,
  });
